import Link from "next/link";
import { Box, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the 3-2-1 Tuesday Ballpark Pass?",
    answer:
      "The 3-2-1 Tuesday Ballpark Pass gives you the ability to attend all 12 regular season Tuesday home games throughout the 2026 season.",
  },
  {
    question: "When is the check-in window?",
    answer:
      "The check-in window opens at 9am on the day of the game. You will be notified via text when the period is open.",
  },
  {
    question: "Where can I go with a Standing Room Only Ticket?",
    answer:
      "With a Standing Room Only Ticket, you can enjoy the game from designated standing areas throughout Great American Ball Park, including concourses down the first and third base line, our Budweiser Bowtie Bar located in right field, and at the top of section 145 underneath the Power Stacks.",
  },
  {
    question: "Does the 3-2-1 Tuesday Ballpark Pass include an assigned seat?",
    answer:
      "The ACCESS pass will not include an assigned seat. Pass holders do have the ability to upgrade to an assigned seat at an additional cost, subject to availability.",
  },
  {
    question: "How do I upgrade my seats?",
    answer: `After confirming your attendance on the day of the game, you'll have the opportunity to purchase upgraded tickets. After confirming attendance simply select the "Add-Ons" option below your game to see all available upgrades. Subject to availability.`,
  },
  {
    question: "When do I need to request guest tickets?",
    answer:
      "Guest tickets need to be requested no later than four hours before scheduled first pitch.",
  },
  {
    question: "Where are my tickets delivered?",
    answer:
      "All of your tickets will be delivered digitally via the MLB Ballpark app.",
  },
  {
    question: "When will my tickets appear in my MLB Ballpark App?",
    answer:
      "Tickets will be delivered to your MLB Ballpark App account 3 hours prior to first pitch.",
  },
  {
    question: "Are there any penalties if I do not opt in?",
    answer:
      "There are no penalties for not attending a game, however you do need to confirm your attendance (opt-in) to receive a ticket.",
  },
  {
    question:
      "Can 3-2-1 Tuesday Ballpark Pass tickets be transferred to a friend?",
    answer:
      "Passes cannot be shared/forwarded to other parties. Admission to Great American Ball Park will be managed through the MLB Ballpark app on your mobile device. Multiple packages can be purchased if you and the rest of your party enter at the same time.",
  },
  {
    question:
      "Can I sell my 3-2-1 Tuesday Ballpark Pass for games that I do not attend?",
    answer:
      "No, 3-2-1 Tuesday Ballpark Passes are non-transferable and are only available on the phone of the 3-2-1 Tuesday Ballpark Pass purchaser. 3-2-1 Tuesday Ballpark Passes cannot be resold. Violators will be removed from the program.",
  },
  {
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your 3-2-1 Tuesday Ballpark Pass ticket will be valid for the rescheduled game only.",
  },
  {
    question:
      "Will I be considered a Season Ticket Member? Do I have access to Season Ticket Member events?",
    answer:
      "No, 3-2-1 Tuesday Ballpark Pass holders are not eligible for Season Ticket Member benefits. Tickets are not transferable and cannot be exchanged for a later date.",
  },
  {
    question: "Does the 3-2-1 Tuesday Ballpark Pass include postseason access?",
    answer:
      "No, the 3-2-1 Tuesday Ballpark Pass is not considered a season plan and does not guarantee any postseason access.",
  },
  {
    question: "Is there any loaded concession value on a Ballpark Pass ticket?",
    answer:
      "Each 3-2-1 Tuesday Ballpark Pass ticket will NOT include any loaded concession value.",
  },
  {
    question:
      "Are any other events included with the 3-2-1 Tuesday Ballpark Pass?",
    answer:
      "The 3-2-1 Tuesday Ballpark Pass is only valid for regular-season home games. It does not include tickets to non-game events.",
  },
  {
    question: "How can I get a refund and cancel my 3-2-1 Tuesday pass?",
    answer: (
      <>
        By selecting the Place Order button, you agree to purchase the tickets
        you have selected. No Refunds. No Exchanges. Please visit{" "}
        <Link
          target="_blank"
          href="https://www.mlb.com/reds/tickets/info/terms-conditions"
        >
          Reds.com/terms
        </Link>{" "}
        for more information. You can also contact us at
        <Link href="mailto:ticketservices@reds.com">
          ticketservices@reds.com
        </Link>
        .
      </>
    ),
  },
  {
    question:
      "Why does the log-in page look different when I'm redirected to the buying page?",
    answer:
      "The MLB log-in page may look different, but it's the same log-in process. You can use your MLB account to sign in.",
  },
  {
    question:
      "Can I combine my seats with friends or family if we purchased different seating categories?",
    answer:
      "No. Seating categories cannot be combined. For example, if one fan has a Standing Room Only ticket and another has an Upper Level seat, they cannot be seated together. Each ticket is valid only within its designated seating category.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from{" "}
    <Link href="http://reds.com" target="_blank">
      reds.com
    </Link>
    . You may unsubscribe at any time. By checking this box you agree to receive
    additional commercial emails from reds.com and MLB.com.
    <br />
    The terms and conditions of your ticket and policies applicable to its use
    are updated to address circumstances related to COVID-19 and health and
    safety. By clicking the Checkout button, you agree to accept all Terms and
    Conditions. Further, due to the COVID-19 pandemic, scheduling may be
    impacted; games may be canceled, rescheduled, or postponed or games may be
    held with limited capacity. Upon completing your purchase by selecting
    Checkout you acknowledge and agree that your credit card will be charged.
    <br />
    By clicking Checkout, you agree to the MLB Privacy Policy, Terms of Use and
    to receive your ticket(s) via an SMS text message to the mobile # associated
    with your Reds account. Message and data rates may apply. Tickets will be
    made available in the MLB Ballpark mobile application only.{" "}
    <Link href="http://MLB.com" target="_blank">
      MLB.com
    </Link>{" "}
    and supported mobile device required.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/reds/tickets/info/terms-conditions?partnerId=redirect-cin-terms"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General TicketingTerms & Conditions | Cincinnati Reds
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const HomePageZeroStateDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      You can purchase one{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.landing,
          query: { teamSchema },
        }}
      >
        here
      </Link>
      , or check your{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.purchase_history,
          query: { teamSchema },
        }}
      >
        Purchase History
      </Link>{" "}
      to see passes you’ve gifted.
    </Box>
  );
};

export const RedeemGiftSuccessModalDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      Your gift is all set! Create a{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.my_group,
          query: { teamSchema },
        }}
      >
        group
      </Link>{" "}
      to bring friends along, or explore the schedule in{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.home,
          query: { teamSchema },
        }}
      >
        Upcoming Games
      </Link>
      . We’ll send you an SMS when check-in opens as the season gets closer.
    </Box>
  );
};
